import { useCallback, useEffect, useMemo, useState } from 'react'

import { getMyLedger, getMyTenancies } from '../_data/resident'
import { ResidentLedger, ResidentTenancy } from '../_types/resident'
import { useAuth } from './use-auth'
import {
  ResidentTenancyContext,
  ResidentTenancyContextValue,
} from './use-resident-tenancy'

interface ResidentTenancyProviderProps {
  children: React.ReactNode
}

/**
 * Shared resident-portal state for the QuickPass app. Once the renter is
 * authenticated it loads their tenancies, tracks which tenancy is selected
 * across the resident surfaces (Home / Payments / Maintenance), and keeps the
 * selected tenancy's ledger in one place so the dashboard balance and the
 * payments page never double-fetch or disagree. Non-residents resolve to an
 * empty list (the single getMyTenancies call returns [] and nothing else runs).
 *
 * @param {ResidentTenancyProviderProps} props
 * @returns {JSX.Element}
 */
const ResidentTenancyProvider = ({
  children,
}: ResidentTenancyProviderProps): JSX.Element => {
  const { quickPassUser } = useAuth()
  const [tenancies, setTenancies] = useState<ResidentTenancy[]>([])
  const [isLoadingTenancies, setIsLoadingTenancies] = useState<boolean>(true)
  const [selectedTenancyId, setSelectedTenancyId] = useState<string>('')
  const [ledger, setLedger] = useState<ResidentLedger | null>(null)
  const [isLoadingLedger, setIsLoadingLedger] = useState<boolean>(false)

  const refreshTenancies = useCallback(async (): Promise<void> => {
    if (quickPassUser === null) {
      setTenancies([])
      setSelectedTenancyId('')
      setIsLoadingTenancies(false)
      return
    }

    const loaded = await getMyTenancies()
    setTenancies(loaded)
    setSelectedTenancyId(currentId => {
      const isCurrentStillPresent = loaded.some(
        tenancy => tenancy.id === currentId,
      )
      if (currentId !== '' && isCurrentStillPresent === true) {
        return currentId
      }
      const firstActive = loaded.find(tenancy => tenancy.status === 'active')
      if (firstActive !== undefined) {
        return firstActive.id
      }
      return loaded.length > 0 ? loaded[0].id : ''
    })
    setIsLoadingTenancies(false)
  }, [quickPassUser])

  useEffect(() => {
    setIsLoadingTenancies(true)
    void refreshTenancies()
  }, [refreshTenancies])

  useEffect(() => {
    if (selectedTenancyId === '') {
      setLedger(null)
      setIsLoadingLedger(false)
      return
    }

    let isMounted = true
    setLedger(null)
    setIsLoadingLedger(true)

    void getMyLedger(selectedTenancyId).then(loaded => {
      if (isMounted === false) {
        return
      }
      setLedger(loaded)
      setIsLoadingLedger(false)
    })

    return () => {
      isMounted = false
    }
  }, [selectedTenancyId])

  const selectTenancy = useCallback((tenancyId: string): void => {
    setSelectedTenancyId(tenancyId)
  }, [])

  const selectedTenancy = useMemo<ResidentTenancy | null>(
    () => tenancies.find(tenancy => tenancy.id === selectedTenancyId) ?? null,
    [tenancies, selectedTenancyId],
  )

  const hasResidentAccess = tenancies.length > 0

  const value = useMemo<ResidentTenancyContextValue>(
    () => ({
      tenancies,
      isLoadingTenancies,
      hasResidentAccess,
      selectedTenancyId,
      selectedTenancy,
      selectTenancy,
      refreshTenancies,
      ledger,
      isLoadingLedger,
    }),
    [
      tenancies,
      isLoadingTenancies,
      hasResidentAccess,
      selectedTenancyId,
      selectedTenancy,
      selectTenancy,
      refreshTenancies,
      ledger,
      isLoadingLedger,
    ],
  )

  return (
    <ResidentTenancyContext.Provider value={value}>
      {children}
    </ResidentTenancyContext.Provider>
  )
}

export { ResidentTenancyProvider }
